import { useQuery } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { withErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import TabsCenter from "features/CenterContent/shared/Tabs/TabsCenter";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  BusModule,
  PanelHeaderDivider,
  PanelScrollArea,
} from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/altTransportPanelLayout";
import { BackupPanelActions } from "features/CenterContent/VehicleDetails/BackupAlternativeTransportDetails/BackupPanelActions";
import { INLINE_ACTIONS_MIN_WIDTH } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/ActionsOverflowMenu";
import { MessagesFromHastus } from "features/CenterContent/VehicleDetails/BackupAlternativeTransportDetails/MessagesFromHastus";
import { BackupAlternativeTransportItinerary } from "features/CenterContent/VehicleDetails/shared/BackupAlternativeTransportItinerary";
import OperationalLog from "features/CenterContent/VehicleDetails/shared/OperationalLog";
import { useParams } from "react-router-dom";
import { Module } from "shared/components/Module";
import { Badge, tokens } from "shared/components/Spor";
import { reserveAlternativeTransportSchema } from "shared/types/alternativeTransport";
import { isBackupFromPlan } from "shared/utils/alternativeTransport";
import styled from "styled-components";
import * as z from "zod";

const MODULE_MIN_WIDTH = "912px";
const BackupScrollContent = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  margin-right: -${tokens.default.size.spacing[2.5]};
  display: flex;
  flex-direction: column;
  gap: ${tokens.default.size.spacing[2]};
`;

/**
 * Two modules side by side
 */
const BackupModuleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${tokens.default.size.spacing[2]};
  align-items: start;
  padding-bottom: ${tokens.default.size.spacing[3]};
`;

const PanelScroll = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  container-type: inline-size;
  container-name: backupPage;
`;

/**
 * The backup detail panel takes half the available width on wide screens and the full width on narrow screens.
 */
const PANEL_SPLIT_MIN_WIDTH = `calc(${INLINE_ACTIONS_MIN_WIDTH} * 2)`;

const BackupPanelRow = styled.div`
  flex: 1;
  min-height: 0;
  min-width: ${MODULE_MIN_WIDTH};
  display: flex;
  flex-direction: row;
`;

const BackupPanelColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  flex: 1;

  @container backupPage (width >= ${PANEL_SPLIT_MIN_WIDTH}) {
    flex: 0 0 50%;
  }
`;

const BackupAlternativeTransportDetails = () => {
  const { t } = useTranslation();
  const { vehicleUuid } = useParams();

  const { status, data: backupVehicle } = useQuery({
    queryKey: ["backupDetails", "backup", vehicleUuid],
    refetchInterval: 30000,
    queryFn: async ({ signal }) =>
      axiosClient
        .get<
          z.infer<typeof reserveAlternativeTransportSchema>
        >(`${getBackendUrl()}/alternativeTransports/backup/${vehicleUuid}`, { signal })
        .then((res) => reserveAlternativeTransportSchema.parse(res.data)),
  });

  const sectionState = useMemo(
    () => ({
      fetchStatus: status,
      isEmpty: !backupVehicle,
      emptyReason: "Fant ingen detaljer om bussen",
    }),
    [backupVehicle, status],
  );

  const specification = ((): string | undefined => {
    if (!backupVehicle) return undefined;
    return isBackupFromPlan(backupVehicle) ? "Hastus" : "Drops";
  })();

  return (
    <>
      <TabsCenter isBackUpBus />
      <PanelScroll>
        <BackupPanelRow>
          <BackupPanelColumn>
            <BusModule
              sectionState={sectionState}
              title={
                backupVehicle
                  ? `${t("Beredskap")} ${backupVehicle.workShiftId}`
                  : undefined
              }
              specification={specification}
              badgeLeft1={
                backupVehicle && !backupVehicle.isAvailable ? (
                  <Badge colorPalette="blue">{t("I bruk")}</Badge>
                ) : undefined
              }
              action1={
                backupVehicle ? (
                  <BackupPanelActions vehicle={backupVehicle} />
                ) : undefined
              }
            >
              {backupVehicle && (
                <>
                  <PanelHeaderDivider>
                    <BackupAlternativeTransportItinerary
                      vehicle={backupVehicle}
                    />
                  </PanelHeaderDivider>
                  <BackupScrollContent>
                    <PanelScrollArea>
                      <BackupModuleGrid>
                        <Module title={t("Logg")}>
                          <OperationalLog
                            vehicleId={backupVehicle.id}
                            logs={backupVehicle.logs ?? []}
                          />
                        </Module>
                        {isBackupFromPlan(backupVehicle) && (
                          <Module title={t("Meldinger fra Hastus")}>
                            <MessagesFromHastus notes={backupVehicle.notes} />
                          </Module>
                        )}
                      </BackupModuleGrid>
                    </PanelScrollArea>
                  </BackupScrollContent>
                </>
              )}
            </BusModule>
          </BackupPanelColumn>
        </BackupPanelRow>
      </PanelScroll>
    </>
  );
};

export default withErrorBoundary(BackupAlternativeTransportDetails);
